import { useEffect, useMemo, useState } from 'react';
import { Button, Modal, Typography } from 'antd';
import { useLogout } from '@/features/auth';
import { useNotificationContext } from '@/features/notifications';
import {
  useDeleteMyAccount,
  useGetMyAccount,
  UserAccountResponse,
  useUpdateMyAccount,
} from '@/shared/api';
import { Col, Row } from '@/shared/ui';
import { captureError } from '@/shared/utils/error-reporting';

const { Title, Text } = Typography;

export const DeleteAccount = () => {
  const { showNotification } = useNotificationContext();
  const { logout } = useLogout();

  const [modalOpen, setModalOpen] = useState(false);
  const [escCount, setEscCount] = useState(0);

  const { data: profileData, refetch: refechProfile } =
    useGetMyAccount<UserAccountResponse>({});
  const { mutate: deleteAccount, isLoading: deleteLoading } =
    useDeleteMyAccount();
  const { mutate: updateAccount } = useUpdateMyAccount();

  const devModeEnabled = useMemo(
    () => Boolean(profileData?.profile.developerMode),
    [profileData?.profile.developerMode],
  );

  const handleAccountDelete = async () => {
    try {
      deleteAccount(
        {},
        {
          onSuccess: () => {
            setModalOpen(false);
            showNotification({
              message: 'Account deleted successfully.',
              description: 'You will be logged out in a few seconds.',
              type: 'success',
            });
            setTimeout(() => {
              logout();
            }, 3000);
          },
        },
      );
    } catch (error) {
      captureError(error, {
        module: 'delete_account_on_error_handle_account_delete',
        action: 'handle_account_delete',
      });
    }
  };

  const handleDevModeChange = async (devMode: boolean) => {
    updateAccount(
      {
        body: {
          profile: {
            developerMode: !devMode,
          },
        },
      },
      {
        onSuccess: () => {
          refechProfile();
          showNotification({
            message: `Developer mode ${devMode ? 'disabled' : 'enabled'}`,
            description: `Press Esc 10 times to ${
              devMode ? 'enable' : 'disable'
            } developer mode.`,
            type: 'success',
          });
        },
        onError: (error: any) => {
          captureError(error, {
            module: 'delete_account_on_error_handle_dev_mode_change',
            action: 'handle_dev_mode_change',
          });
          showNotification({
            message: `Error ${
              devMode ? 'disabling' : 'enabling'
            } developer mode`,
            description: error.message,
            type: 'error',
          });
        },
      },
    );
  };

  useEffect(() => {
    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setEscCount((prevCount) => prevCount + 1);
      } else {
        setEscCount(0); // Reset count if a different key is pressed
      }
    };

    window.addEventListener('keydown', handleKeydown);

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener('keydown', handleKeydown);
    };
  }, []);

  useEffect(() => {
    if (escCount >= 10) {
      handleDevModeChange(Boolean(profileData?.profile.developerMode));
      setEscCount(0); // Reset the counter after enabling dev mode
    }
  }, [escCount, profileData?.profile.developerMode]);

  return (
    <>
      <Col alignItems="flex-start">
        <Title level={3} style={{ marginBottom: 5 }}>
          Delete account
        </Title>
        <Text>
          By deleting your account you will lose all your data and will not be
          able to recover it.
        </Text>
        <Button
          type="primary"
          danger
          size="large"
          onClick={() => setModalOpen(true)}
          style={{ marginTop: 40 }}
        >
          Delete my account
        </Button>
      </Col>
      {devModeEnabled && (
        <Col alignItems="flex-start" style={{ marginTop: 60 }}>
          <Title level={4} style={{ opacity: 0.6 }}>
            Developer mode enabled
          </Title>
          <Text style={{ opacity: 0.6 }}>
            Press the <strong>Esc</strong> key 10 times to disable developer
            mode.
          </Text>
        </Col>
      )}
      <Modal
        title={
          <Title
            level={2}
            style={{ fontWeight: 300, lineHeight: 1, padding: '30px 0 0 0' }}
          >
            Delete account
          </Title>
        }
        open={modalOpen}
        footer={null}
        style={{ maxWidth: '90%', width: 450 }}
        onCancel={() => setModalOpen(false)}
      >
        <Text>
          Are you sure you want to delete your account?{' '}
          <strong>This action is irreversible</strong>.<br />
          <br />
          You will be logged out.
        </Text>
        <Row justifyContent="flex-end" gap={15} style={{ marginTop: 40 }}>
          <Button size="large" onClick={() => setModalOpen(false)}>
            Cancel
          </Button>
          <Button
            type="primary"
            size="large"
            danger
            loading={deleteLoading}
            onClick={handleAccountDelete}
          >
            Delete my account
          </Button>
        </Row>
      </Modal>
    </>
  );
};
